/** Your passkeys - add one, see them, remove one (ADR-0105).
 *
 * **Said plainly on the screen: new, and not yet tried on every device.** The ADR's last section
 * is the reason: the server has met only a software authenticator. Telling a person "your passkey
 * will work everywhere" would be the claim the register refuses (C7), so the panel says the
 * password and authenticator code keep working and asks nothing to be given up.
 *
 * **Adding one asks for the password again**, in the same form, because a session on its own must
 * not be able to plant a credential that would later satisfy the second factor. The wrong-password
 * refusal is a 403 and is worded beside the field, like the change-password form (a 401 would swap
 * in the sign-in form - the one remedy that cannot help somebody who mistyped a field).
 *
 * **Removing one is a real button with a real refusal.** The server will not remove the last way
 * to sign in; the panel says that sentence rather than hiding the button on a guess about what the
 * server will do.
 *
 * The list is read through `Loaded`: a failed read is "could not be read", never "you have no
 * passkeys" - the sentence a worried person most wants to hear and has no reason to believe from a
 * request that failed.
 */

import { useState } from "react";
import type { FormEvent } from "react";

import { useAddPasskey, usePasskeys, useRemovePasskey } from "../api/passkeys";
import type { Passkey } from "../api/passkeys";
import { instant, useReader } from "../api/profile";
import { Refused } from "../api/refusal";
import { PasskeyBrowserError, passkeysSupported } from "../api/webauthn";
import { FieldError } from "../components/FieldError";
import { Loaded, WriteFailed } from "../components/Loaded";
import { useFocusRefusal } from "../components/useFocusRefusal";
import { useFormChecks } from "../components/useFormChecks";
import own from "./Profile.module.css";
import styles from "./Screen.module.css";

/** Where an add refusal is worded. Anything not listed is shown as a general failure, not under a
 * field it may not be about. */
const PASSWORD_REFUSALS = new Set(["current_password_incorrect", "credential_locked"]);

function browserSentence(error: unknown): string | null {
  if (!(error instanceof PasskeyBrowserError)) return null;
  switch (error.kind) {
    case "cancelled":
      return "The passkey prompt was closed before it finished. Nothing was added.";
    case "already_registered":
      return "This device already has a passkey for your account. Nothing was added.";
    case "unsupported":
      return "This browser cannot make a passkey here. Nothing was added.";
    default:
      return "Your device could not make a passkey. Nothing was added.";
  }
}

function PasskeyRow({
  passkey,
  onRemove,
  pending,
}: {
  passkey: Passkey;
  onRemove: (id: string) => void;
  pending: boolean;
}) {
  const reader = useReader();
  return (
    <li className={own.session} data-testid="passkey">
      <span className={own.sessionFacts}>
        <span data-testid="passkey-label">{passkey.label}</span>
        <span>Added {instant(passkey.created_at, reader)}</span>
        <span>
          {passkey.last_used_at
            ? `Last used ${instant(passkey.last_used_at, reader)}`
            : "Not used to sign in yet"}
        </span>
        <span>{passkey.synced ? "May be synced between your devices" : "Stays on one device"}</span>
      </span>
      <button
        type="button"
        className={styles.fixLink}
        disabled={pending}
        data-testid="passkey-remove"
        onClick={() => onRemove(passkey.id)}
      >
        Remove
      </button>
    </li>
  );
}

function AddPasskey() {
  const add = useAddPasskey();
  const [label, setLabel] = useState("");
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);

  const refusal = add.error instanceof Refused ? add.error : null;
  const passwordRefused = refusal !== null && PASSWORD_REFUSALS.has(refusal.code);
  const browser = browserSentence(add.error);
  const [checks, attach] = useFormChecks();
  useFocusRefusal(checks.element, add.error);

  function submit(event: FormEvent) {
    event.preventDefault();
    setDone(false);
    // Pressing the button says what is missing; it is never greyed to say nothing (G-P8-03).
    const ok = checks.run([
      { field: "label", ok: label.trim() !== "", message: "Name this passkey, such as Work laptop." },
      { field: "password", ok: password !== "", message: "Type your current password." },
    ]);
    if (!ok) return;
    add.mutate(
      { currentPassword: password, label: label.trim() },
      {
        onSuccess: () => {
          setLabel("");
          setPassword("");
          setDone(true);
        },
      },
    );
  }

  return (
    <form ref={attach} className={own.form} onSubmit={submit} noValidate data-testid="passkey-add-form">
      <label className={own.field}>
        Name this passkey
        <input
          value={label}
          maxLength={80}
          placeholder="Work laptop"
          autoComplete="off"
          {...checks.field("label")}
          data-testid="passkey-label-input"
          onChange={(event) => {
            checks.touch("label");
            setLabel(event.target.value);
            setDone(false);
          }}
        />
        <FieldError {...checks.say("label")} />
      </label>
      <label className={own.field}>
        Your current password
        <input
          type="password"
          value={password}
          autoComplete="current-password"
          {...checks.field("password")}
          aria-invalid={passwordRefused || checks.errors.password !== undefined ? true : undefined}
          data-testid="passkey-password-input"
          onChange={(event) => {
            checks.touch("password");
            setPassword(event.target.value);
            setDone(false);
          }}
        />
        <FieldError {...checks.say("password")} />
      </label>
      {passwordRefused ? (
        <p className={own.refused} role="alert" data-testid="passkey-password-refused">
          {refusal.message}
        </p>
      ) : null}
      {browser ? (
        <p className={own.refused} role="alert" data-testid="passkey-browser-refused">
          {browser}
        </p>
      ) : null}
      {/* Everything that is neither the password nor the browser: too many passkeys, an expired
          ceremony, a dropped connection. */}
      {!passwordRefused && !browser ? <WriteFailed error={add.error} of="your passkey" /> : null}
      {done && !add.isPending ? (
        <p className={own.saved} role="status" data-testid="passkey-added">
          Passkey added. Next time, choose “Use a passkey” on the sign-in page.
        </p>
      ) : null}
      <span>
        <button
          type="submit"
          className={styles.primary}
          disabled={add.isPending}
          data-testid="passkey-add"
        >
          {add.isPending ? "Waiting for your device…" : "Add a passkey"}
        </button>
      </span>
    </form>
  );
}

export function PasskeyPanelBody() {
  const passkeys = usePasskeys();
  const remove = useRemovePasskey();
  const supported = passkeysSupported();

  return (
    <>
      <Loaded query={passkeys} of="Your passkeys" empty="You have not added a passkey.">
        {(rows) => (
          <ul className={own.sessions} data-testid="passkeys">
            {rows.map((row) => (
              <PasskeyRow
                key={row.id}
                passkey={row}
                pending={remove.isPending}
                onRemove={(id) => remove.mutate(id)}
              />
            ))}
          </ul>
        )}
      </Loaded>
      <WriteFailed
        error={remove.error}
        of="that passkey"
        onReload={() => {
          remove.reset();
          void passkeys.refetch();
        }}
      />
      {supported ? (
        <AddPasskey />
      ) : (
        <p className={styles.note} data-testid="passkey-unsupported">
          This browser cannot make a passkey.
        </p>
      )}
    </>
  );
}
